import { err, ok, type Result } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';
import { runInAction } from 'mobx';

import type { EditorStore } from '@src/editor';
import type { RendererState } from '@src/renderer/RendererState';

export type TrackLayerEditorStoreLike = Pick<
  EditorStore,
  'shiftTrackLayerUp' | 'shiftTrackLayerDown' | 'tracksMap'
>;

export interface TrackLayerVideoCreatorLike {
  selectedTrack: number;
}

export interface TrackLayerVideoSourceLike {
  onNextStateChange: (
    listener: (state: RendererState) => void | Promise<void>,
  ) => () => void;
}

export interface TrackLayerServiceDeps {
  editorStore: TrackLayerEditorStoreLike;
  videoCreator?: TrackLayerVideoCreatorLike;
  videoSource?: TrackLayerVideoSourceLike;
}

/**
 * TrackLayerService centralizes layer reordering for timeline tracks.
 * It relies on the editor store command surface but emits structured Result objects
 * so callers can surface errors consistently (keyboard shortcuts, UI buttons, etc.).
 */
export class TrackLayerService {
  private readonly _editorStore: TrackLayerEditorStoreLike;
  private readonly _videoCreator?: TrackLayerVideoCreatorLike;
  private readonly _videoSource?: TrackLayerVideoSourceLike;
  private readonly _logger = LogManager.getLogger('service:TrackLayerService');

  constructor(deps: TrackLayerServiceDeps) {
    this._editorStore = deps.editorStore;
    this._videoCreator = deps.videoCreator;
    this._videoSource = deps.videoSource;
  }

  layerUp(trackId: string): Result<{ trackId: string }> {
    if (!trackId) {
      return err('VALIDATION', 'Track id is required for layer up');
    }

    try {
      const targetTrackId = this._getAdjacentTrackId(trackId, 'up');
      const shifted = this._editorStore.shiftTrackLayerUp(trackId);
      if (!shifted) {
        this._logger.warn('Track could not be shifted up', { trackId });
        return err('PRECONDITION', 'Track cannot move up from current layer');
      }

      this._scheduleSelectedTrackUpdate(trackId, targetTrackId);
      return ok({ trackId });
    } catch (error) {
      this._logger.error('Failed to shift track layer up', { trackId, error });
      return err('UNKNOWN', 'Unexpected failure moving track layer up', error);
    }
  }

  layerDown(trackId: string): Result<{ trackId: string }> {
    if (!trackId) {
      return err('VALIDATION', 'Track id is required for layer down');
    }

    try {
      const targetTrackId = this._getAdjacentTrackId(trackId, 'down');
      const shifted = this._editorStore.shiftTrackLayerDown(trackId);
      if (!shifted) {
        this._logger.warn('Track could not be shifted down', { trackId });
        return err('PRECONDITION', 'Track cannot move down from current layer');
      }

      this._scheduleSelectedTrackUpdate(trackId, targetTrackId);
      return ok({ trackId });
    } catch (error) {
      this._logger.error('Failed to shift track layer down', {
        trackId,
        error,
      });
      return err(
        'UNKNOWN',
        'Unexpected failure moving track layer down',
        error,
      );
    }
  }

  private _getAdjacentTrackId(
    trackId: string,
    direction: 'up' | 'down',
  ): string | null {
    const tracks = this._editorStore.tracksMap;
    if (!tracks || !tracks.size) return null;
    const trackIds = [...tracks.keys()].sort((a, b) => a - b);
    const numericId = Number(trackId);
    if (!Number.isFinite(numericId)) return null;
    const idx = trackIds.indexOf(numericId);
    if (idx === -1) return null;
    const neighborIndex = direction === 'up' ? idx + 1 : idx - 1;
    const neighbor = trackIds[neighborIndex];
    return neighbor == null ? null : String(neighbor);
  }

  private _scheduleSelectedTrackUpdate(
    previousTrackId: string,
    newTrackId: string | null,
  ): void {
    if (!this._videoCreator || newTrackId == null) return;
    const selected = this._videoCreator.selectedTrack;
    if (Number(selected) !== Number(previousTrackId)) return;
    const applySelection = () => {
      const creator = this._videoCreator;
      if (!creator) return;
      runInAction(() => {
        creator.selectedTrack = Number(newTrackId);
      });
    };

    if (this._videoSource) {
      this._videoSource.onNextStateChange(state => {
        void state;
        applySelection();
      });
      return;
    }

    this._defer(() => applySelection());
  }

  private _defer(callback: () => void): void {
    const scheduleMicrotask = () => {
      if (typeof queueMicrotask === 'function') {
        queueMicrotask(callback);
      } else {
        Promise.resolve()
          .then(callback)
          .catch(err => {
            this._logger.warn('Deferred track selection update failed', {
              err,
            });
          });
      }
    };

    if (
      typeof window !== 'undefined' &&
      typeof window.requestAnimationFrame === 'function'
    ) {
      window.requestAnimationFrame(() => {
        scheduleMicrotask();
      });
      return;
    }
    scheduleMicrotask();
  }
}
